/**
 * @fileoverview Download trigger and export error helpers for Reports.
 * @module pages/reports/reportExportUtils
 */
import { toast } from "react-toastify";
import { logger, sanitizeError } from "../../utils/logger.js";
import { getApiErrorMessage } from "../../utils/apiErrorMessages.js";

/** Starts a browser download when the S3 link is a valid HTTP(S) URL. */
export const startReportDownload = (s3Link, filename) => {
  if (typeof s3Link !== "string" || !/^https?:\/\//i.test(s3Link)) {
    return false;
  }
  const anchor = document.createElement("a");
  anchor.href = s3Link;
  anchor.download = filename;
  anchor.click();
  return true;
};

/** Maps API export errors to localized toast messages. */
export const notifyReportExportError = (error, fallbackType, toastIdRef) => {
  const knownCodes = [
    "FORBIDDEN",
    "UNAUTHORIZED",
    "NO_APPOINTMENTS",
    "NO_INVOICES",
    "NO_COMPANIES",
    "NO_COMPANIES_DATA",
    "NO_EVENTS_DATA",
    "NO_REQUIREMENTS",
    "NO_ATTENDANCES",
  ];
  const errorType = knownCodes.includes(error?.code) ? error.code : fallbackType;
  toastIdRef.current = errorType;
  toast.error(getApiErrorMessage({ errorType }), {
    toastId: toastIdRef.current,
  });
};

/** Shared try/catch wrapper for report export handlers. */
export const runReportExport = async ({
  isValid,
  missingFieldsMessage,
  exportFn,
  filename,
  errorFallback,
  setLoadingExportLogs,
  toastIdRef,
  failureLogMessage = missingFieldsMessage,
}) => {
  if (!isValid()) {
    logger.error(missingFieldsMessage);
    return;
  }

  try {
    setLoadingExportLogs(true);
    const s3Link = await exportFn();
    if (!startReportDownload(s3Link, filename)) {
      logger.error(failureLogMessage);
    }
  } catch (error) {
    notifyReportExportError(error, errorFallback, toastIdRef);
    logger.error("Error", { err: sanitizeError(error) });
  } finally {
    setLoadingExportLogs(false);
  }
};
